import { Head, Link, router } from '@inertiajs/react'
import Pagination from "@/components/pagination.tsx";

import { CategoryTaxType, CategoryType, PaginationType } from './types.ts'
import { ArrowLeft, Edit, Tag, FileText, Users, DollarSign, Trash2 } from "lucide-react";

interface ShowProps {
  category: CategoryType
  can_edit: boolean
  can_destroy?: boolean
  total_taxes: number
  total_tax_payers: number
  total_amount_received: number
  taxes: CategoryTaxType[]
  taxes_pagination: PaginationType
}

export default function Show({
  category,
  can_edit,
  can_destroy = false,
  total_taxes,
  total_tax_payers,
  total_amount_received,
  taxes,
  taxes_pagination
}: ShowProps) {

  const handleDestroy = () => {
    if (!window.confirm(`Supprimer la catégorie "${category.name}" ?`)) return

    router.delete(`/admin/categories/${category.id}`)
  }

  return (
    <>
      <Head title={`Catégorie #${category.id}`} />

      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/admin/categories">
              <button className="rounded-lg border border-border p-2 text-foreground hover:bg-secondary transition-colors">
                <ArrowLeft className="h-5 w-5" />
              </button>
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-3">
                  <Tag className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{category.name}</h1>
                  <p className="text-sm text-muted-foreground">{category.code}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
          {can_edit && (
              <Link href={`/admin/categories/${category.id}/edit`}>
                <button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
                  <Edit className="h-4 w-4" />
                  Editer
                </button>
              </Link>
          )}
          {can_destroy && (
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg border border-destructive px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
              onClick={handleDestroy}
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </button>
          )}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Taxes</p>
                <p className="mt-2 text-3xl font-bold text-foreground">{total_taxes}</p>
              </div>
              <FileText className="h-8 w-8 text-primary" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total des contribuables</p>
                <p className="mt-2 text-3xl font-bold text-foreground">{total_tax_payers}</p>
              </div>
              <Users className="h-8 w-8 text-info" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Amount Received</p>
                <p className="mt-2 text-3xl font-bold text-success">{total_amount_received.toLocaleString()} FCFA</p>
              </div>
              <DollarSign className="h-8 w-8 text-success" />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold text-foreground">Détails</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Nom</p>
              <p className="text-sm font-medium text-foreground">{category.name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Code</p>
              <p className="text-sm font-medium text-foreground">{category.code}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Statut</p>
              <p className="text-sm font-medium text-foreground">{category.active ? "Actif" : "Inactif"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Description</p>
              <p className="text-sm font-medium text-foreground">{category.description || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tax Module</p>
              <p className="text-sm font-medium text-foreground">{category.tax_module_name || "-"}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold text-foreground">Taxes In This Catégorie</h2>
          <div className="mt-4 overflow-hidden rounded-lg border border-border">
            <table className="w-full">
              <thead className="border-b border-border bg-secondary">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Nom</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Code</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Statut</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Action</th>
              </tr>
              </thead>
              <tbody className="divide-y divide-border">
              {taxes.length > 0 ? taxes.map((tax) => (
                <tr key={tax.id} className="hover:bg-secondary/50">
                  <td className="px-4 py-3 text-sm text-foreground">{tax.name}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{tax.code}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${tax.active ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                      {tax.active ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Link href={`/admin/taxes/${tax.id}`} className="text-primary hover:text-primary/80">
                      Voir
                    </Link>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td className="px-4 py-6 text-sm text-muted-foreground" colSpan={4}>
                    Aucune taxe trouvée pour cette catégorie.
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <Pagination
              currentPage={taxes_pagination.current_page}
              totalPages={taxes_pagination.total_pages}
              basePath={`/admin/categories/${category.id}`}
            />
          </div>
        </div>
      </div>
    </>
  )
}
